import { getSearchData } from '@yandex-int/k-common/client/api';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b from 'b_';
import { CloseKit, Search } from 'education-icons';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { Input, type InputChangeEvent, Popup, useGeneratePath, useIntl, useStaticUrls } from 'platform-components';
import { EduPopup } from 'platform-components/common';
import { INPUT_THEMES } from 'platform-components/constants';
import { getMe } from 'platform-components/src/components/user/selectors';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import ym from 'react-yandex-metrika';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { LibrarySearchContent } from './__content/search__content';
import type { SearchResults, SearchResultItem, SearchFilters } from './typings';
import { getLibrarySearchResults, getSearchState } from './utils';

import './search.scss';

interface Props {
    namespaceIds: Array<number>;
    params?: Record<string, string>;
    filters?: SearchFilters;
}

export const LibrarySearch = ({ namespaceIds, params = {}, filters }: Props) => {
    const [searchResults, setSearchResults] = useState<SearchResults>({});
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [loadingStatus, setLoadingStatus] = useState<LOADING_STATUSES>(LOADING_STATUSES.UNSENT);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const popupRef = useRef<Popup>(null);
    const owner = React.useRef(null);
    const wrapper = React.useRef(null);
    const intl = useIntl();
    const history = useHistory();
    const staticUrls = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const me = useSelector(getMe);
    const { pathname, search } = useLocation();
    const { classId = null } = useParams<Record<string, string>>();

    const getSearchResults = async (value: string) => {
        try {
            if (value) {
                setLoadingStatus(LOADING_STATUSES.LOADING);
                ym('reachGoal', 'search_request_text', {
                    page: pathname,
                    request_text: value,
                    role: 'teacher',
                    teacherId: me?.id,
                });
                const res = await getSearchData({ text: value, namespaceIds, ...params });

                setSearchResults(getLibrarySearchResults(res, filters));
                setLoadingStatus(LOADING_STATUSES.SUCCESS);
            } else {
                setSearchResults({});
                setLoadingStatus(LOADING_STATUSES.UNSENT);
            }
        } catch (e) {
            setSearchResults({});
            setLoadingStatus(LOADING_STATUSES.ERROR);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceOnChange = useCallback(debounce(getSearchResults, 400), []);

    useEffect(() => {
        popupRef.current?.setOwner(owner.current);
    }, [owner]);

    const handleInputChange = ({ target: { value } }: InputChangeEvent) => {
        if (!searchQuery && value) {
            ym('reachGoal', 'search_start', { page: pathname, role: 'teacher', teacherId: me?.id });
        } else if (searchQuery && !value) {
            ym('reachGoal', 'search_delete_query', {
                method: 'button',
                page: pathname,
                role: 'teacher',
                teacherId: me?.id,
            });
        }

        setSearchQuery(value);

        debounceOnChange(value);
    };

    const handleSearchResultClick = ({ subject, themeId, themeName, number }: SearchResultItem) => {
        ym('reachGoal', 'search_choice', {
            document_subject: subject,
            document_id: themeId,
            document_name: themeName,
            batch_number: number,
            page: pathname,
            request_text: searchQuery,
            role: 'teacher',
            teacherId: me?.id,
        });

        setSearchQuery(themeName);
        setSearchResults({});
        setIsFocused(false);

        const path = classId ? staticUrls.labLibrarySubjectTheme : staticUrls.deepLabTheme;

        history.push(`${getRouterPath(path, { subject, themeId })}${search}`);
    };

    const handleClear = () => {
        ym('reachGoal', 'search_delete_query', {
            method: 'cross',
            page: pathname,
            role: 'teacher',
            teacherId: me?.id,
        });

        setSearchQuery('');
        setSearchResults({});
        setIsFocused(true);
    };

    const handleFocus = async () => {
        setIsFocused(true);

        if (searchQuery && isEmpty(searchResults)) {
            await getSearchResults(searchQuery);
        }
    };

    const searchState = getSearchState(searchQuery, searchResults, loadingStatus);
    const shouldShowPopup = isFocused;
    const shouldShowClearControl = Boolean(searchQuery);

    if (namespaceIds.length === 0) {
        return null;
    }

    return (
        <>
            <div className={b('library', 'search-overlay', { visible: shouldShowPopup })} />
            <div className={b('library', 'search', { active: shouldShowPopup })} ref={wrapper}>
                <div className={b('library', 'search-input-wrapper', { active: shouldShowPopup })} ref={owner}>
                    <Search className="library__search-icon" />
                    <Input
                        focused={isFocused}
                        mix="library__search-input"
                        onBlur={() => setIsFocused(false)}
                        onChange={handleInputChange}
                        onFocus={handleFocus}
                        placeholder={intl.formatMessage({ id: 'library.search.inputPlaceholder' })}
                        theme={INPUT_THEMES.BORDERLESS}
                        value={searchQuery}
                    />
                    {shouldShowClearControl && (
                        <div className="library__clear-search" {...accessibleClick(handleClear)}>
                            <CloseKit />
                        </div>
                    )}
                </div>
                <EduPopup
                    anchor={owner}
                    className="library__search-popup"
                    direction={['bottom']}
                    scope={wrapper}
                    target="anchor"
                    view="default"
                    visible={shouldShowPopup}
                >
                    <div className="library__search-popup-content">
                        <LibrarySearchContent
                            onResultClick={handleSearchResultClick}
                            searchResults={searchResults}
                            searchState={searchState}
                        />
                    </div>
                </EduPopup>
            </div>
        </>
    );
};
